import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { RotateCcw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createNadDebug } from '../../../../utils/debugLog'
import { cn } from '../../../../lib/utils'
import type { LocationPanoramaViewDict } from '../../../../generated/nodeDataTypes'
import { CANVAS_LOCAL_WHEEL_TARGET_ATTR } from '../../canvasWheelScope'
import {
  PANORAMA_DEFAULT_VIEW,
  PANORAMA_MAX_FOV_DEGREES,
  PANORAMA_MAX_PITCH_DEGREES,
  PANORAMA_MIN_FOV_DEGREES,
  PANORAMA_MIN_PITCH_DEGREES,
  normalizePanoramaView,
  wrapPanoramaYaw,
} from './panoramaView'

const CANVAS_WIDTH = 960
const CANVAS_HEIGHT = 540
const MAX_SOURCE_WIDTH = 3840
const MAX_SOURCE_HEIGHT = 1920
const DRAG_YAW_SCALE = 0.25
const DRAG_PITCH_SCALE = 0.18
const WHEEL_FOV_SCALE = 0.04
const WHEEL_COMMIT_DELAY_MS = 180
const FOCUSABLE_DIALOG_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const panoramaLog = createNadDebug('dw:location-panorama-viewer')

type NormalizedPanoramaView = Required<LocationPanoramaViewDict>

function getFocusableDialogElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_DIALOG_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.tabIndex >= 0,
  )
}

interface LocationPanoramaControlsProps {
  value: NormalizedPanoramaView
  onDraftChange: (view: NormalizedPanoramaView) => void
  onCommit: (view: NormalizedPanoramaView) => void
  compact?: boolean
}

function numberFromRange(value: string, fallback: number): number {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

function viewWith(
  view: NormalizedPanoramaView,
  patch: Partial<NormalizedPanoramaView>,
): NormalizedPanoramaView {
  return normalizePanoramaView({ ...view, ...patch })
}

function getBoundedSourceSize(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_SOURCE_WIDTH / width, MAX_SOURCE_HEIGHT / height)
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export const LocationPanoramaControls = memo(function LocationPanoramaControls({
  value,
  onDraftChange,
  onCommit,
  compact = false,
}: LocationPanoramaControlsProps) {
  const { t } = useTranslation('canvas')
  const latestValueRef = useRef(value)

  useEffect(() => {
    latestValueRef.current = value
  }, [value])

  const renderSlider = (
    key: keyof NormalizedPanoramaView,
    label: string,
    min: number,
    max: number,
    step: number,
  ) => (
    <label className="block" data-testid={`location-panorama-${key}`}>
      <span className="mb-1 flex items-center justify-between text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums text-foreground">{Math.round(value[key])}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[key]}
        aria-label={label}
        className="h-1 w-full cursor-pointer accent-primary focus-visible:ring-2 focus-visible:ring-primary/50"
        onChange={(event) => {
          const next = viewWith(value, {
            [key]: numberFromRange(event.currentTarget.value, value[key]),
          })
          latestValueRef.current = next
          onDraftChange(next)
        }}
        onPointerUp={() => onCommit(latestValueRef.current)}
        onKeyUp={(event) => {
          if (event.key.startsWith('Arrow')) onCommit(latestValueRef.current)
        }}
        onBlur={() => onCommit(latestValueRef.current)}
      />
    </label>
  )

  return (
    <div className={cn('flex flex-col gap-3', compact && 'gap-2')}>
      {renderSlider(
        'yaw_degrees',
        t('locationDesignerNode.panoramaYaw'),
        -180,
        179,
        1,
      )}
      {renderSlider(
        'pitch_degrees',
        t('locationDesignerNode.panoramaPitch'),
        PANORAMA_MIN_PITCH_DEGREES,
        PANORAMA_MAX_PITCH_DEGREES,
        1,
      )}
      {renderSlider(
        'fov_degrees',
        t('locationDesignerNode.panoramaFov'),
        PANORAMA_MIN_FOV_DEGREES,
        PANORAMA_MAX_FOV_DEGREES,
        1,
      )}
      <div className="flex items-center justify-start gap-2">
        <button
          type="button"
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          onClick={() => {
            onDraftChange(PANORAMA_DEFAULT_VIEW)
            onCommit(PANORAMA_DEFAULT_VIEW)
          }}
          data-testid="location-panorama-reset"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('locationDesignerNode.panoramaReset')}
        </button>
      </div>
    </div>
  )
})

interface LocationPanoramaViewerProps {
  imageUrl: string
  view: NormalizedPanoramaView
  nodeId: string
  onDraftChange: (view: NormalizedPanoramaView) => void
  onCommit: (view: NormalizedPanoramaView) => void
  onClose: () => void
}

interface LocationPanoramaViewportProps {
  imageUrl: string
  view: NormalizedPanoramaView
  nodeId: string
  onDraftChange: (view: NormalizedPanoramaView) => void
  onCommit: (view: NormalizedPanoramaView) => void
  /** Inline node activation, kept separate from the isolated rotation gesture. */
  onActivate?: () => void
  className?: string
  canvasClassName?: string
  testId?: string
  onLoadStateChange?: (state: 'loading' | 'ready' | 'error') => void
}

function drawPanoramaFrame(
  source: ImageData,
  output: ImageData,
  view: NormalizedPanoramaView,
): void {
  const sourceWidth = source.width
  const sourceHeight = source.height
  const outWidth = output.width
  const outHeight = output.height
  const sourcePixels = source.data
  const outputPixels = output.data
  const yaw = (view.yaw_degrees * Math.PI) / 180
  const pitch = (view.pitch_degrees * Math.PI) / 180
  const halfHorizontalFov = (view.fov_degrees * Math.PI) / 360
  const aspect = outWidth / outHeight
  const tanHalfHorizontal = Math.tan(halfHorizontalFov)
  const tanHalfVertical = tanHalfHorizontal / aspect
  const cosYaw = Math.cos(yaw)
  const sinYaw = Math.sin(yaw)
  const cosPitch = Math.cos(pitch)
  const sinPitch = Math.sin(pitch)

  for (let y = 0; y < outHeight; y += 1) {
    const cameraY = (1 - 2 * ((y + 0.5) / outHeight)) * tanHalfVertical
    for (let x = 0; x < outWidth; x += 1) {
      const cameraX = (2 * ((x + 0.5) / outWidth) - 1) * tanHalfHorizontal
      const length = Math.sqrt(cameraX * cameraX + cameraY * cameraY + 1)
      const vx = cameraX / length
      const vy = cameraY / length
      const vz = 1 / length
      const pitchedY = vy * cosPitch - vz * sinPitch
      const pitchedZ = vy * sinPitch + vz * cosPitch
      const worldX = vx * cosYaw + pitchedZ * sinYaw
      const worldZ = -vx * sinYaw + pitchedZ * cosYaw
      const longitude = Math.atan2(worldX, worldZ)
      const latitude = Math.asin(Math.max(-1, Math.min(1, pitchedY)))
      const srcX = Math.floor(((longitude / (2 * Math.PI)) + 0.5) * sourceWidth) % sourceWidth
      const srcY = Math.max(
        0,
        Math.min(sourceHeight - 1, Math.floor((0.5 - latitude / Math.PI) * sourceHeight)),
      )
      const sourceOffset = (srcY * sourceWidth + srcX) * 4
      const outputOffset = (y * outWidth + x) * 4
      outputPixels[outputOffset] = sourcePixels[sourceOffset]
      outputPixels[outputOffset + 1] = sourcePixels[sourceOffset + 1]
      outputPixels[outputOffset + 2] = sourcePixels[sourceOffset + 2]
      outputPixels[outputOffset + 3] = 255
    }
  }
}

export const LocationPanoramaViewport = memo(function LocationPanoramaViewport({
  imageUrl,
  view,
  nodeId,
  onDraftChange,
  onCommit,
  onActivate,
  className,
  canvasClassName,
  testId = 'location-panorama-canvas',
  onLoadStateChange,
}: LocationPanoramaViewportProps) {
  const { t } = useTranslation('canvas')
  const rootRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sourceRef = useRef<ImageData | null>(null)
  const frameRef = useRef<ImageData | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const wheelCommitTimerRef = useRef<number | null>(null)
  const dragRef = useRef<{ pointerId: number; x: number; y: number; view: NormalizedPanoramaView } | null>(null)
  const latestViewRef = useRef(view)
  const onLoadStateChangeRef = useRef(onLoadStateChange)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    onLoadStateChangeRef.current = onLoadStateChange
  }, [onLoadStateChange])

  const setObservedLoadState = useCallback((state: 'loading' | 'ready' | 'error') => {
    setLoadState(state)
    onLoadStateChangeRef.current?.(state)
  }, [])

  useEffect(() => {
    let cancelled = false
    setObservedLoadState('loading')
    sourceRef.current = null
    frameRef.current = null
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    const image = new Image()
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      image.crossOrigin = 'anonymous'
    }
    image.onload = () => {
      if (cancelled) return
      const sourceCanvas = document.createElement('canvas')
      const sourceSize = getBoundedSourceSize(image.naturalWidth, image.naturalHeight)
      sourceCanvas.width = sourceSize.width
      sourceCanvas.height = sourceSize.height
      const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true })
      if (!sourceContext) {
        setObservedLoadState('error')
        return
      }
      try {
        sourceContext.drawImage(image, 0, 0, sourceCanvas.width, sourceCanvas.height)
        sourceRef.current = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height)
      } catch {
        setObservedLoadState('error')
        return
      }
      setObservedLoadState('ready')
      panoramaLog({
        event: 'source-loaded',
        trace_id: `location-panorama-viewer:${nodeId}:viewer`,
        feature: 'location-panorama-viewer',
        node_id: nodeId,
        source_width: sourceCanvas.width,
        source_height: sourceCanvas.height,
        natural_width: image.naturalWidth,
        natural_height: image.naturalHeight,
      })
    }
    image.onerror = () => {
      if (!cancelled) setObservedLoadState('error')
    }
    image.src = imageUrl
    return () => {
      cancelled = true
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (wheelCommitTimerRef.current !== null) {
        window.clearTimeout(wheelCommitTimerRef.current)
        wheelCommitTimerRef.current = null
      }
    }
  }, [imageUrl, nodeId, setObservedLoadState])

  const renderScheduledView = useCallback(() => {
    animationFrameRef.current = null
    if (loadState !== 'ready') return
    const canvas = canvasRef.current
    const source = sourceRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context || !source) return
    if (canvas.width !== CANVAS_WIDTH) canvas.width = CANVAS_WIDTH
    if (canvas.height !== CANVAS_HEIGHT) canvas.height = CANVAS_HEIGHT

    if (!frameRef.current) {
      frameRef.current = context.createImageData(CANVAS_WIDTH, CANVAS_HEIGHT)
    }
    const frame = frameRef.current
    const renderedView = latestViewRef.current
    drawPanoramaFrame(source, frame, renderedView)
    context.putImageData(frame, 0, 0)
    panoramaLog({
      event: 'view-rendered',
      trace_id: `location-panorama-viewer:${nodeId}:view`,
      feature: 'location-panorama-viewer',
      node_id: nodeId,
      yaw_degrees: Math.round(renderedView.yaw_degrees),
      pitch_degrees: Math.round(renderedView.pitch_degrees),
      fov_degrees: Math.round(renderedView.fov_degrees),
    })
  }, [loadState, nodeId])

  const schedulePanoramaRender = useCallback(() => {
    if (animationFrameRef.current !== null) return
    animationFrameRef.current = window.requestAnimationFrame(renderScheduledView)
  }, [renderScheduledView])

  useEffect(() => {
    latestViewRef.current = view
    if (loadState === 'ready') schedulePanoramaRender()
  }, [loadState, schedulePanoramaRender, view])

  const commit = useCallback(
    (next: NormalizedPanoramaView) => {
      onCommit(next)
      panoramaLog({
        event: 'view-commit',
        trace_id: `location-panorama-viewer:${nodeId}:view`,
        feature: 'location-panorama-viewer',
        node_id: nodeId,
        yaw_degrees: Math.round(next.yaw_degrees),
        pitch_degrees: Math.round(next.pitch_degrees),
        fov_degrees: Math.round(next.fov_degrees),
      })
    },
    [nodeId, onCommit],
  )

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (wheelCommitTimerRef.current !== null) {
        window.clearTimeout(wheelCommitTimerRef.current)
        wheelCommitTimerRef.current = null
      }
    }
  }, [])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (event.button !== 0) return
      // Rotation owns this gesture, but consuming pointerdown also suppresses
      // ReactFlow's selection. Activate the inline owner before isolating it.
      onActivate?.()
      event.preventDefault()
      event.stopPropagation()
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
      dragRef.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        view,
      }
    },
    [onActivate, view],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      event.preventDefault()
      event.stopPropagation()
      const next = normalizePanoramaView({
        yaw_degrees: wrapPanoramaYaw(drag.view.yaw_degrees - (event.clientX - drag.x) * DRAG_YAW_SCALE),
        pitch_degrees: drag.view.pitch_degrees + (event.clientY - drag.y) * DRAG_PITCH_SCALE,
        fov_degrees: drag.view.fov_degrees,
      })
      latestViewRef.current = next
      onDraftChange(next)
    },
    [onDraftChange],
  )

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      event.preventDefault()
      event.stopPropagation()
      dragRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      panoramaLog({
        event: 'panorama:rotate-drag-end',
        trace_id: `location-panorama-viewer:${nodeId}:view`,
        feature: 'location-panorama-viewer',
        node_id: nodeId,
        surface: onActivate ? 'inline-node' : 'fullscreen',
        yaw_from: drag.view.yaw_degrees,
        yaw_to: latestViewRef.current.yaw_degrees,
        pitch_from: drag.view.pitch_degrees,
        pitch_to: latestViewRef.current.pitch_degrees,
      })
      commit(latestViewRef.current)
    },
    [commit, nodeId, onActivate],
  )

  const applyWheelZoom = useCallback(
    (deltaY: number, deltaMode: number) => {
      const deltaModeFactor = deltaMode === 1 ? 16 : deltaMode === 2 ? 120 : 1
      const next = viewWith(latestViewRef.current, {
        fov_degrees: latestViewRef.current.fov_degrees + deltaY * deltaModeFactor * WHEEL_FOV_SCALE,
      })
      latestViewRef.current = next
      onDraftChange(next)
      if (wheelCommitTimerRef.current !== null) {
        window.clearTimeout(wheelCommitTimerRef.current)
      }
      wheelCommitTimerRef.current = window.setTimeout(() => {
        wheelCommitTimerRef.current = null
        commit(latestViewRef.current)
      }, WHEEL_COMMIT_DELAY_MS)
      panoramaLog({
        event: 'wheel-zoom',
        trace_id: `location-panorama-viewer:${nodeId}:view`,
        feature: 'location-panorama-viewer',
        node_id: nodeId,
        fov_degrees: Math.round(next.fov_degrees),
      })
    },
    [commit, nodeId, onDraftChange],
  )

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const handleNativeWheel = (event: WheelEvent) => {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      applyWheelZoom(event.deltaY, event.deltaMode)
    }
    root.addEventListener('wheel', handleNativeWheel, { capture: true, passive: false })
    return () => root.removeEventListener('wheel', handleNativeWheel, true)
  }, [applyWheelZoom])

  const content = useMemo(() => {
    if (loadState === 'error') {
      return (
        <div
          role="alert"
          className="flex h-full w-full items-center justify-center bg-surface-0 text-sm text-muted-foreground"
        >
          {t('locationDesignerNode.panoramaLoadFailed')}
        </div>
      )
    }
    return (
      <>
        <canvas
          ref={canvasRef}
          className={cn(
            'block h-full w-full cursor-grab touch-none bg-surface-0 object-cover active:cursor-grabbing',
            loadState === 'loading' && 'opacity-30',
            canvasClassName,
          )}
          aria-label={t('locationDesignerNode.panoramaCanvas')}
          data-testid={testId}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
        {loadState === 'loading' && (
          <div
            role="status"
            className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground"
          >
            {t('locationDesignerNode.panoramaLoading')}
          </div>
        )}
      </>
    )
  }, [
    canvasClassName,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    loadState,
    t,
    testId,
  ])

  return (
    <div
      ref={rootRef}
      className={cn('relative overflow-hidden bg-surface-0 nopan nodrag nowheel', className)}
      {...{ [CANVAS_LOCAL_WHEEL_TARGET_ATTR]: 'true' }}
      onPointerDown={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      {content}
    </div>
  )
})

export const LocationPanoramaViewer = memo(function LocationPanoramaViewer({
  imageUrl,
  view,
  nodeId,
  onDraftChange,
  onCommit,
  onClose,
}: LocationPanoramaViewerProps) {
  const { t } = useTranslation('canvas')
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElementRef = useRef<Element | null>(null)
  const previousBodyOverflowRef = useRef<string>('')
  const latestViewRef = useRef(view)

  useEffect(() => {
    latestViewRef.current = view
  }, [view])

  useEffect(() => {
    panoramaLog({
      event: 'viewer-open',
      trace_id: `location-panorama-viewer:${nodeId}:viewer`,
      feature: 'location-panorama-viewer',
      node_id: nodeId,
    })
  }, [nodeId])

  const commit = useCallback(
    (next: NormalizedPanoramaView) => {
      latestViewRef.current = next
      onCommit(next)
      panoramaLog({
        event: 'view-commit',
        trace_id: `location-panorama-viewer:${nodeId}:view`,
        feature: 'location-panorama-viewer',
        node_id: nodeId,
        yaw_degrees: Math.round(next.yaw_degrees),
        pitch_degrees: Math.round(next.pitch_degrees),
        fov_degrees: Math.round(next.fov_degrees),
      })
    },
    [nodeId, onCommit],
  )

  const handleDraftChange = useCallback(
    (next: NormalizedPanoramaView) => {
      latestViewRef.current = next
      onDraftChange(next)
    },
    [onDraftChange],
  )

  const handleClose = useCallback(() => {
    commit(latestViewRef.current)
    onClose()
  }, [commit, onClose])

  useEffect(() => {
    previousActiveElementRef.current = document.activeElement
    previousBodyOverflowRef.current = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.setTimeout(() => closeButtonRef.current?.focus(), 0)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
        return
      }
      if (event.key !== 'Tab') return

      const dialog = dialogRef.current
      if (!dialog) return
      const focusableElements = getFocusableDialogElements(dialog)
      const first = focusableElements[0]
      const last = focusableElements[focusableElements.length - 1]
      if (!first || !last) {
        event.preventDefault()
        dialog.focus()
        return
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousBodyOverflowRef.current
      const previousActiveElement = previousActiveElementRef.current
      if (previousActiveElement instanceof HTMLElement) {
        previousActiveElement.focus()
      }
    }
  }, [handleClose])

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 p-8 nopan nodrag nowheel"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation()
        if (event.target === event.currentTarget) handleClose()
      }}
      data-testid="location-panorama-viewer"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="flex max-h-full w-full max-w-6xl flex-col gap-3"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2
              id={titleId}
              className="rounded-full border border-border-subtle bg-surface-2 px-2 py-1 text-xs font-medium text-foreground"
            >
              {t('locationDesignerNode.panoramaTitle')}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            onClick={handleClose}
            aria-label={t('locationDesignerNode.panoramaClose')}
            data-testid="location-panorama-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
          <LocationPanoramaViewport
            imageUrl={imageUrl}
            view={view}
            nodeId={nodeId}
            onDraftChange={handleDraftChange}
            onCommit={commit}
            className="aspect-video rounded-xl border border-border"
          />
          <div className="rounded-xl border border-border bg-surface-1 p-3">
            <LocationPanoramaControls
              value={view}
              onDraftChange={handleDraftChange}
              onCommit={commit}
            />
          </div>
        </div>
      </div>
    </div>
  )
})
